import { useMemo } from "react";

import type { GetDaoCoinMarketFeesResponse } from "@/backend/deso";
import { APP_NAME_GENERIC, OPENFUND_APP_NAME } from "@/utils/constants";
import { basisPointsToPercent } from "@/utils/currency";
import type { PartialAccount } from "@/utils/types";
import { getUserName } from "@/utils/usernames";

interface PricePerCoinTooltipProps {
  account: PartialAccount;
  loading: boolean;
  creatorCoinMarketFee: GetDaoCoinMarketFeesResponse | null;
}

const PricePerCoinTooltip = ({
  account,
  loading,
  creatorCoinMarketFee,
}: PricePerCoinTooltipProps) => {
  const pricePerCoinTooltip = useMemo(() => {
    const accountPublicKey = account.publicKey ?? "";
    const focusAppKey = process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY;
    const openfundAppKey = process.env.NEXT_PUBLIC_OPENFUND_PROFILE_PUBLIC_KEY;

    const accountFee =
      creatorCoinMarketFee?.FeeBasisPointsByPublicKey[accountPublicKey] ?? 0;
    const focusAppFee =
      creatorCoinMarketFee?.FeeBasisPointsByPublicKey[focusAppKey] ?? 0;
    const openfundAppFee =
      creatorCoinMarketFee?.FeeBasisPointsByPublicKey[openfundAppKey] ?? 0;

    const otherFees = Object.entries(
      creatorCoinMarketFee?.FeeBasisPointsByPublicKey || {},
    )
      .filter(
        ([key]) =>
          key !== account?.publicKey &&
          key !== focusAppKey &&
          key !== openfundAppKey,
      )
      .reduce((total, [, fee]) => total + fee, 0);

    return (
      <section>
        <div>
          This price includes all trading fees associated with this purchase, as
          shown below:
        </div>

        <ul className="mt-2 ml-4 list-disc">
          {!loading &&
            (creatorCoinMarketFee ? (
              <>
                {account && (
                  <li>
                    {getUserName({ account, customPrefix: "" })}:{" "}
                    {basisPointsToPercent(accountFee)}%
                  </li>
                )}

                <li>
                  {APP_NAME_GENERIC}: {basisPointsToPercent(focusAppFee)}%
                </li>

                <li>
                  {OPENFUND_APP_NAME}: {basisPointsToPercent(openfundAppFee)}%
                </li>

                {otherFees > 0 && (
                  <li>Referrer: {basisPointsToPercent(otherFees)}%</li>
                )}
              </>
            ) : (
              <li>0.00%</li>
            ))}
        </ul>
      </section>
    );
  }, [account, creatorCoinMarketFee, loading]);

  return <div>{pricePerCoinTooltip}</div>;
};

export default PricePerCoinTooltip;
